import financialOperationsServices from '@/api/financialOperationsServices';
import { RootState } from '@/app/store';
import { FinancialOperation } from '@/interfaces/financialOperations';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface FinancialOperationsState {
  value: { [key: string]: FinancialOperation[] };
  byReference: { [key: string]: FinancialOperation[] };
  currFilters: string;
  cbsReference: string;
  loading: boolean;
  error: string;
}

const initialState: FinancialOperationsState = {
  value: {},
  byReference: {},
  cbsReference: '',
  currFilters: '',
  loading: false,
  error: '',
};

export const fetchFinancialOperations = createAsyncThunk('financialOperations/operations', async ({ filters = {} }: { filters?: any }) => {
  const result = await financialOperationsServices.financialOperations({ ...filters });
  return result;
});

export const fetchMyFinancialOperations = createAsyncThunk(
  'myFinancialOperations/operations',
  async ({ filters = {} }: { filters?: any }) => {
    const result = await financialOperationsServices.myTransactions({ ...filters });
    return result;
  }
);

export const financialOperationsSlice = createSlice({
  name: 'financialOperation',
  initialState,
  reducers: {
    refresh: () => initialState,

    handleParamsChange: (state, action: PayloadAction<{ accountId: string }>) => {
      const filters = JSON.stringify(action.payload);
      state.currFilters = filters;
    },

    handleCbsReference: (state, action: PayloadAction<{ cbsReference: string }>) => {
      const {
        payload: { cbsReference },
      } = action;
      state.cbsReference = cbsReference;
    },

    deleteFinancialOperation: (state, action: PayloadAction<{ ids: string[] }>) => {
      const {
        payload: { ids },
      } = action;
      state.value[state.currFilters] = state.value[state.currFilters]?.filter((rec: FinancialOperation) => !ids.includes(rec.operationId));
      state.byReference[state.cbsReference] = state.byReference[state.cbsReference]?.filter(
        (rec: FinancialOperation) => !ids.includes(rec.operationId)
      );
    },

    updateFinancialOperation: (state, action: PayloadAction<{ ids: string[]; user: any }>) => {
      const {
        payload: { ids, user },
      } = action;
      const validator = { userId: user.id, approvedAt: '', firstName: user.firstName, lastName: user.lastName };
      ids.forEach(id => {
        state.value[state.currFilters]?.find((rec: FinancialOperation) => rec.operationId === id)?.validators.push(validator);
        state.byReference[state.cbsReference]?.find((rec: FinancialOperation) => rec.operationId === id)?.validators.push(validator);
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchFinancialOperations.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchFinancialOperations.fulfilled, (state, action) => {
      const {
        payload: { data },
        meta: { arg },
      } = action;
      const { cbsReference } = arg.filters;
      if (cbsReference) {
        state.byReference[cbsReference] = data;
      } else {
        state.value[state.currFilters] = data;
      }
      state.loading = false;
    });
    builder.addCase(fetchFinancialOperations.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
    builder.addCase(fetchMyFinancialOperations.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchMyFinancialOperations.fulfilled, (state, action) => {
      const {
        payload: { data },
        meta: { arg },
      } = action;
      const { cbsReference } = arg.filters;
      state.byReference[cbsReference] = data;
      state.loading = false;
    });
    builder.addCase(fetchMyFinancialOperations.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
  },
});

export const { handleParamsChange, deleteFinancialOperation, updateFinancialOperation, refresh, handleCbsReference } =
  financialOperationsSlice.actions;

export const financialOperations = (state: RootState) => state.financialOperations;

export default financialOperationsSlice.reducer;
